<template>
  <a-popup :visible="visible" @cancel="$emit('close')">
    <div class="tw-mx-auto tw-w-[95%] tw-max-w-2xl tw-bg-white tw-rounded-lg tw-shadow-xl tw-overflow-hidden">
      <!-- Header -->
      <div class="tw-p-4 sm:tw-p-6 tw-border-b tw-border-gray-200">
        <div class="tw-flex tw-items-start tw-justify-between tw-gap-4">
          <div class="tw-min-w-0 tw-flex-1">
            <h2 class="tw-text-lg sm:tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-2 tw-line-clamp-2">
              {{ meetingData?.title || notification?.title }}
            </h2>
            
            <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600">
              <span v-if="meetingData?.meeting_type" class="tw-font-medium">
                {{ getMeetingTypeText(meetingData.meeting_type) }}
              </span>
              <span v-if="meetingData?.organizer" class="tw-flex tw-items-center tw-gap-1">
                <icon icon="solar:user-bold" class="tw-text-xs" />
                {{ meetingData.organizer.name }}
              </span>
              <span v-if="meetingStatus" class="tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full" :class="getStatusBadgeClass(meetingData?.status)">
                {{ getStatusText(meetingData?.status) }}
              </span>
            </div>
          </div>
          
          <button @click="$emit('close')" class="tw-p-2 tw-rounded-lg tw-bg-gray-100 hover:tw-bg-gray-200 tw-transition-colors tw-shrink-0">
            <icon icon="eva:close-fill" class="tw-text-lg tw-text-gray-500" />
          </button>
        </div>
      </div>
      
      <!-- Content -->
      <div class="tw-p-4 sm:tw-p-6 tw-space-y-4">
        <!-- Date, Time & Location -->
        <div class="tw-space-y-3">
          <!-- Date & Time -->
          <div class="tw-flex tw-items-start tw-gap-3 tw-text-sm">
            <icon icon="solar:calendar-bold" class="tw-text-indigo-500 tw-mt-0.5 tw-shrink-0" />
            <div>
              <div class="tw-font-medium tw-text-gray-900">{{ formatMeetingDate(meetingData?.start_time) }}</div>
              <div class="tw-text-gray-600">
                {{ formatMeetingTime(meetingData?.start_time) }} - {{ formatMeetingTime(meetingData?.end_time) }}
                <span v-if="meetingData?.duration_minutes" class="tw-ml-2 tw-text-gray-500">
                  ({{ formatDuration(meetingData.duration_minutes) }})
                </span>
              </div>
            </div>
          </div>
          
          <!-- Location -->
          <div class="tw-flex tw-items-start tw-gap-3 tw-text-sm">
            <icon :icon="isVirtualMeeting ? 'solar:monitor-bold' : 'solar:map-point-bold'" class="tw-text-emerald-500 tw-mt-0.5 tw-shrink-0" />
            <div>
              <div class="tw-font-medium tw-text-gray-900">
                {{ meetingData?.location || t('common.no-location', 'No location specified') }}
              </div>
              <div v-if="isVirtualMeeting" class="tw-text-gray-500 tw-text-xs tw-mt-1">
                {{ t('common.virtual-meeting', 'Virtual Meeting') }}
              </div>
            </div>
          </div>
          
          <!-- Countdown for upcoming meetings -->
          <div v-if="timeUntilMeeting && meetingData?.status === 'upcoming'" class="tw-p-3 tw-bg-amber-50 tw-rounded-lg tw-border tw-border-amber-200">
            <div class="tw-flex tw-items-center tw-gap-2 tw-text-amber-700 tw-text-sm">
              <icon icon="solar:bell-bing-bold" class="tw-shrink-0" />
              <span class="tw-font-medium">{{ timeUntilMeeting }}</span>
            </div>
          </div>
        </div>
        
        <!-- Description -->
        <div v-if="meetingData?.description" class="tw-space-y-2">
          <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">{{ t('common.description', 'Description') }}</h3>
          <p class="tw-text-sm tw-text-gray-600 tw-leading-relaxed">{{ meetingData.description }}</p>
        </div>
        
        <!-- Participants (Simplified) -->
        <div v-if="meetingData?.participants?.length" class="tw-space-y-2">
          <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">
            {{ t('common.participants', 'Participants') }} ({{ meetingData.participants.length }})
          </h3>
          <div class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600">
            <div class="tw-flex -tw-space-x-1">
              <div v-for="(participant) in meetingData.participants.slice(0, 3)" :key="participant.id" 
                class="tw-w-6 tw-h-6 tw-bg-gradient-to-br tw-from-indigo-400 tw-to-purple-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-text-xs tw-font-medium tw-border tw-border-white">
                {{ getInitials(participant.name) }}
              </div>
              <div v-if="meetingData.participants.length > 3" 
                class="tw-w-6 tw-h-6 tw-bg-gray-300 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-gray-600 tw-text-xs tw-font-medium tw-border tw-border-white">
                +{{ meetingData.participants.length - 3 }}
              </div>
            </div>
            <span>{{ meetingData.participants.slice(0, 2).map(p => p.name).join(', ') }}
              <span v-if="meetingData.participants.length > 2">{{ t('common.and-others', 'and others') }}</span>
            </span>
          </div>
        </div>
        
        <!-- Agenda (Simplified) -->
        <div v-if="meetingData?.agenda?.length" class="tw-space-y-2">
          <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">
            {{ t('common.agenda', 'Agenda') }} ({{ meetingData.agenda.length }} {{ t('common.items', 'items') }})
          </h3>
          <div class="tw-text-sm tw-text-gray-600">
            <div class="tw-truncate">{{ meetingData.agenda[0] }}</div>
            <div v-if="meetingData.agenda.length > 1" class="tw-text-xs tw-text-gray-500 tw-mt-1">
              {{ t('common.and-more-items', 'and {count} more items', { count: meetingData.agenda.length - 1 }) }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- Footer Actions -->
      <div class="tw-p-4 sm:tw-p-6 tw-border-t tw-border-gray-100 tw-bg-gray-50">
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3">
          <!-- Primary Action -->
          <template v-if="meetingData?.status === 'scheduled'">
            <button @click="acceptMeeting"
              class="tw-flex-1 tw-px-4 tw-py-3 tw-bg-emerald-500 tw-text-white hover:tw-bg-emerald-600 tw-rounded-lg tw-transition-colors tw-font-medium tw-text-sm tw-flex tw-items-center tw-justify-center tw-gap-2">
              <icon icon="solar:check-circle-bold" />
              {{ t('buttons.accept', 'Accept') }}
            </button>
            
            <button @click="declineMeeting"
              class="tw-flex-1 tw-px-4 tw-py-3 tw-bg-white tw-text-rose-600 tw-border tw-border-rose-200 hover:tw-bg-rose-50 tw-rounded-lg tw-transition-colors tw-font-medium tw-text-sm tw-flex tw-items-center tw-justify-center tw-gap-2">
              <icon icon="solar:close-circle-bold" />
              {{ t('buttons.decline', 'Decline') }}
            </button>
          </template>
          
          <template v-else-if="meetingData?.status === 'upcoming' && meetingData?.meeting_url">
            <button @click="joinMeeting"
              class="tw-flex-1 tw-px-4 tw-py-3 tw-bg-indigo-500 tw-text-white hover:tw-bg-indigo-600 tw-rounded-lg tw-transition-colors tw-font-medium tw-text-sm tw-flex tw-items-center tw-justify-center tw-gap-2">
              <icon icon="solar:videocamera-record-linear" />
              {{ t('buttons.join-now', 'Join Now') }}
            </button>
          </template>
          
          <!-- Close button -->
          <button @click="$emit('close')"
            class="tw-px-4 tw-py-3 tw-bg-white tw-text-gray-600 tw-border tw-border-gray-200 hover:tw-bg-gray-50 tw-rounded-lg tw-transition-colors tw-font-medium tw-text-sm">
            {{ t('buttons.close', 'Close') }}
          </button>
        </div>
      </div>
    </div>
  </a-popup>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue';
import { useI18n } from "vue-i18n";

const { t } = useI18n();

// Define props
const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  notification: {
    type: Object,
    required: true
  }
});

// Define emits
const emit = defineEmits(['close', 'meetingAccepted', 'meetingDeclined', 'meetingJoined']);

// Extract meeting data
const meetingData = computed(() => {
  return props.notification?.data?.meeting || null;
});

// Meeting status
const meetingStatus = computed(() => {
  return meetingData.value?.status || null;
});

// Check if it's a virtual meeting
const isVirtualMeeting = computed(() => {
  const location = meetingData.value?.location?.toLowerCase() || '';
  return location.includes('zoom') || location.includes('teams') || location.includes('meet') || 
         location.includes('webex') || meetingData.value?.meeting_url;
});

// Calculate time until meeting
const timeUntilMeeting = computed(() => {
  if (!meetingData.value?.start_time) return null;
  
  const now = new Date();
  const meetingStart = new Date(meetingData.value.start_time);
  const diffMs = meetingStart - now;
  
  if (diffMs <= 0) return null;
  
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffDays > 0) {
    return t('time.starts-in-days', { count: diffDays }, `Starts in ${diffDays} day${diffDays > 1 ? 's' : ''}`);
  } else if (diffHours > 0) {
    return t('time.starts-in-hours', { count: diffHours }, `Starts in ${diffHours} hour${diffHours > 1 ? 's' : ''}`);
  } else if (diffMins > 0) {
    return t('time.starts-in-minutes', { count: diffMins }, `Starts in ${diffMins} minute${diffMins > 1 ? 's' : ''}`);
  } else {
    return t('time.starting-soon', 'Starting soon');
  }
});

// Helper functions
function getInitials(name) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function formatMeetingDate(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return t('common.today', 'Today');
    }
    
    if (date.toDateString() === tomorrow.toDateString()) {
      return t('common.tomorrow', 'Tomorrow');
    }
    
    // Check if the date string has a timezone indicator (Z suffix)
    const isUTC = typeof dateString === 'string' && dateString.includes('Z');
    
    if (isUTC) {
      // For UTC dates, use UTC methods to avoid timezone conversion
      const month = date.toLocaleString(undefined, { month: 'long' });
      const day = date.getUTCDate();
      const year = date.getUTCFullYear();
      return `${month} ${day}, ${year}`;
    } else {
      // For local dates, use local methods
      return date.toLocaleDateString(undefined, { 
        weekday: 'long', 
        year: 'numeric',
        month: 'long', 
        day: 'numeric' 
      });
    }
  } catch (error) {
    console.error('Error formatting meeting date:', error);
    return '';
  }
}

function formatMeetingTime(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if the date string has a timezone indicator (Z suffix)
    const isUTC = typeof dateString === 'string' && dateString.includes('Z');
    
    if (isUTC) {
      // For UTC dates, use UTC methods to avoid timezone conversion
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${displayHours}:${displayMinutes} ${ampm}`;
    } else {
      // For local dates, use local methods
      return date.toLocaleTimeString(undefined, { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true
      });
    }
  } catch (error) {
    console.error('Error formatting meeting time:', error);
    return '';
  }
}

function formatDuration(minutes) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${minutes}m`;
}

function getMeetingTypeText(type) {
  const typeMap = {
    'recurring': t('meeting-types.recurring', 'Recurring Meeting'),
    'presentation': t('meeting-types.presentation', 'Presentation'),
    'planning': t('meeting-types.planning', 'Planning Meeting'),
    'review': t('meeting-types.review', 'Review Meeting'),
    'standup': t('meeting-types.standup', 'Standup Meeting'),
    'workshop': t('meeting-types.workshop', 'Workshop'),
    'training': t('meeting-types.training', 'Training Session')
  };
  
  return typeMap[type] || t('meeting-types.meeting', 'Meeting');
}

function getStatusText(status) {
  const statusMap = {
    'scheduled': t('meeting-status.scheduled', 'Scheduled'),
    'upcoming': t('meeting-status.upcoming', 'Starting Soon'),
    'completed': t('meeting-status.completed', 'Completed'),
    'cancelled': t('meeting-status.cancelled', 'Cancelled'),
    'in_progress': t('meeting-status.in-progress', 'In Progress')
  };
  
  return statusMap[status] || status;
}

function getStatusBadgeClass(status) {
  const statusClasses = {
    'scheduled': 'tw-bg-blue-100 tw-text-blue-700',
    'upcoming': 'tw-bg-amber-100 tw-text-amber-700',
    'completed': 'tw-bg-emerald-100 tw-text-emerald-700',
    'cancelled': 'tw-bg-red-100 tw-text-red-700',
    'in_progress': 'tw-bg-purple-100 tw-text-purple-700'
  };
  
  return statusClasses[status] || 'tw-bg-gray-100 tw-text-gray-700';
}

// Action handlers
function acceptMeeting() {
  console.log('Meeting accepted:', meetingData.value);
  emit('meetingAccepted', meetingData.value);
  emit('close');
}

function declineMeeting() {
  console.log('Meeting declined:', meetingData.value);
  emit('meetingDeclined', meetingData.value);
  emit('close');
}

function joinMeeting() {
  if (meetingData.value?.meeting_url) {
    console.log('Joining meeting:', meetingData.value);
    window.open(meetingData.value.meeting_url, '_blank');
    emit('meetingJoined', meetingData.value);
  }
}
</script> 